import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { useTabState } from "@/hooks/use-tab-state";
import { ArrowLeft, Trash2, Loader2 } from "lucide-react";
import type { Facility, Deal } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { InlineTextField } from "@/components/inline-edit/inline-text-field";
import { InlineSelectField } from "@/components/inline-edit/inline-select-field";
import { InlineDateField } from "@/components/inline-edit/inline-date-field";
import { FacilitySecuritiesPanel } from "@/components/facilities/facility-securities-panel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

const STATUS_OPTIONS = [
  { value: "indicative", label: "Indicative" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "settled", label: "Settled" },
  { value: "declined", label: "Declined" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "scenario_not_proceeding", label: "Scenario Not Proceeding" },
];

const STATUS_COLORS: Record<string, string> = {
  indicative: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  submitted: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  settled: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  declined: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  withdrawn: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  scenario_not_proceeding: "bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "-";
  const date = typeof value === "string" ? new Date(value) : value;
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function FacilityDetail() {
  const [, params] = useRoute("/facilities/:id");
  const [, navigate] = useLocation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useTabState("overview");
  const { toast } = useToast();

  const { data: facility, isLoading } = useQuery<Facility>({
    queryKey: ["/api/crm/facilities", params?.id],
    enabled: !!params?.id,
  });

  const { data: deal } = useQuery<Deal>({
    queryKey: ["/api/crm/deals", facility?.dealId],
    enabled: !!facility?.dealId,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Facility>) => {
      const response = await apiRequest("PATCH", `/api/crm/facilities/${params?.id}`, updates);
      return response;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["/api/crm/facilities", params?.id] });
      const previousFacility = queryClient.getQueryData(["/api/crm/facilities", params?.id]);
      if (previousFacility) {
        queryClient.setQueryData(["/api/crm/facilities", params?.id], {
          ...previousFacility,
          ...updates,
        });
      }
      return { previousFacility };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousFacility) {
        queryClient.setQueryData(["/api/crm/facilities", params?.id], context.previousFacility);
      }
      toast({
        title: "Error",
        description: error.message || "Failed to save changes",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities", params?.id] });
    },
  });

  const handleFieldUpdate = async (field: keyof Facility, value: any): Promise<void> => {
    await updateMutation.mutateAsync({ [field]: value });
  };

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/crm/facilities/${params?.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities"] });
      toast({
        title: "Facility deleted",
        description: "The facility has been successfully deleted.",
      });
      navigate("/facilities");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete facility",
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
    setIsDeleteDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-3 text-sm">The facility you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/facilities")} size="sm">Back to Facilities</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <DetailPageShell
        title={facility.facilityName}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Facilities", href: "/facilities" },
          { label: facility.facilityName },
        ]}
        value={activeTab}
        onValueChange={setActiveTab}
        badge={
          <Badge
            variant="secondary"
            className={`capitalize text-xs ${STATUS_COLORS[facility.facilityStatus || "indicative"]}`}
          >
            {(facility.facilityStatus || "indicative").replace("_", " ")}
          </Badge>
        }
        navItems={[
          { label: "Overview", href: "#overview" },
          { label: "Securities", href: "#securities" },
        ]}
        rightPanel={null}
        actions={
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/facilities")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        }
        tabs={[
          {
            value: "overview",
            label: "Overview",
            content: (
              <div id="overview" className="space-y-2">
                <Card>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      {/* Facility Details */}
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Facility Name</p>
                        <InlineTextField
                          value={facility.facilityName}
                          onSave={(value) => handleFieldUpdate("facilityName", value)}
                          placeholder="Facility name"
                        />
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Deal</p>
                        <p className="text-sm">
                          {deal ? deal.dealName : facility.dealId}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Facility Type</p>
                        <InlineTextField
                          value={facility.facilityType || ""}
                          onSave={(value) => handleFieldUpdate("facilityType", value || null)}
                          placeholder="e.g., Term Loan, Overdraft"
                        />
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Status</p>
                        <InlineSelectField
                          value={facility.facilityStatus || "indicative"}
                          options={STATUS_OPTIONS}
                          onSave={(value) => handleFieldUpdate("facilityStatus", value)}
                        />
                      </div>

                      {/* Amounts */}
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Requested Amount</p>
                        <p className="text-sm font-medium">{formatCurrency(facility.requestedAmount)}</p>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Approved Limit</p>
                        <p className="text-sm font-medium">{formatCurrency(facility.approvedLimit)}</p>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Funded Amount</p>
                        <p className="text-sm font-medium">{formatCurrency(facility.fundedAmount)}</p>
                      </div>

                      {/* v2.0: Interest Rate Display (D-003) */}
                      {facility.totalRate && (
                        <>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Base Rate</p>
                            <p className="text-sm">{facility.baseRate ? `${(parseFloat(facility.baseRate) * 100).toFixed(2)}%` : "-"}</p>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Margin</p>
                            <p className="text-sm">{facility.margin ? `${(parseFloat(facility.margin) * 100).toFixed(2)}%` : "-"}</p>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Total Rate</p>
                            <p className="text-sm font-medium text-green-600">{facility.totalRate ? `${(parseFloat(facility.totalRate) * 100).toFixed(2)}%` : "-"}</p>
                          </div>
                        </>
                      )}

                      {/* v2.0: Term Details (D-003) */}
                      {facility.termMonths && (
                        <>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Term</p>
                            <p className="text-sm">{facility.termMonths ? `${facility.termMonths} months` : "-"}</p>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">I/O Period</p>
                            <p className="text-sm">{facility.ioMonths ? `${facility.ioMonths} months` : "0 months"}</p>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Repayment Frequency</p>
                            <p className="text-sm capitalize">{facility.repaymentFrequency?.replace("_", " ") || "Monthly"}</p>
                          </div>
                        </>
                      )}

                      {/* v2.0: Repayment Calculations (D-003) - Read-only display */}
                      {(facility.ioRepayment || facility.piRepayment) && (
                        <>
                          {facility.ioRepayment && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground mb-1">I/O Repayment</p>
                              <p className="text-sm font-medium text-blue-600">{formatCurrency(facility.ioRepayment)}</p>
                            </div>
                          )}

                          {facility.piRepayment && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground mb-1">P&I Repayment</p>
                              <p className="text-sm font-medium text-blue-600">{formatCurrency(facility.piRepayment)}</p>
                            </div>
                          )}

                          {facility.repaymentOverride && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground mb-1">Repayment Override</p>
                              <p className="text-sm font-medium text-orange-600">{formatCurrency(facility.repaymentOverride)}</p>
                            </div>
                          )}
                        </>
                      )}

                      {/* Dates */}
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Application Date</p>
                        <InlineDateField
                          value={facility.applicationDate || ""}
                          onSave={(value) => handleFieldUpdate("applicationDate", value || null)}
                        />
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Approval Date</p>
                        <InlineDateField
                          value={facility.approvalDate || ""}
                          onSave={(value) => handleFieldUpdate("approvalDate", value || null)}
                        />
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Settlement Date</p>
                        <InlineDateField
                          value={facility.settlementDate || ""}
                          onSave={(value) => handleFieldUpdate("settlementDate", value || null)}
                        />
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Maturity Date</p>
                        <InlineDateField
                          value={facility.maturityDate || ""}
                          onSave={(value) => handleFieldUpdate("maturityDate", value || null)}
                        />
                      </div>

                      {/* Metadata */}
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Created</p>
                        <p className="text-sm">{formatDate(facility.createdAt)}</p>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Last Updated</p>
                        <p className="text-sm">{formatDate(facility.updatedAt)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "securities",
            label: "Securities",
            content: (
              <div id="securities" className="space-y-2">
                <FacilitySecuritiesPanel
                  facilityId={facility.id}
                  dealId={facility.dealId}
                  facilityLimit={facility.approvedLimit}
                  facilityName={facility.facilityName}
                />
              </div>
            ),
          },
        ]}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the facility "{facility.facilityName}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
